import { format } from 'date-fns/format';
import { Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, FormControl, InputGroup, Spinner } from 'react-bootstrap';
import styled from 'styled-components';
import GenericTable from '../../components/GenericTable';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { getScheduledBets } from '../../redux/entities/gameEntity';
import { useAppDispatch } from '../../redux/hooks';

interface ScheduledBet {
  id: string;
  game?: {
    name: string;
  };
  resultTime?: {
    resultTime: string;
  };
  gameType?: string;
  gameConfig?: any;
  betNumber: string;
  betPoint: number;
  status: 'PENDING' | 'PROCESSED' | 'FAILED' | 'CANCELLED' | string;
  createdAt: string;
}

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 15px;
  flex-wrap: wrap;
`;

const SearchBox = styled(InputGroup)`
  max-width: 320px;
  flex-grow: 1;

  @media (max-width: 576px) {
    max-width: 100%;
  }

  .form-control:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
  }
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #d4deea;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(20, 34, 66, 0.08);
  padding: 12px 14px 4px;
  overflow: hidden;
  margin-bottom: 2rem;

  .table {
    margin-bottom: 0.5rem;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid #dce4ef;
    border-radius: 10px;
    overflow: hidden;
  }

  .table thead th {
    background: #f1f6fc;
    color: #2b4866;
    border-top: none;
    border-bottom: 1px solid #d3dfed;
    border-right: 1px solid #dbe5f1;
    font-weight: 700;
    font-size: 0.88rem;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    padding: 12px 10px;
    white-space: nowrap;
  }

  .table thead th:last-child {
    border-right: none;
  }

  .table tbody td {
    border-top: 1px solid #e4ebf3;
    border-right: 1px solid #edf2f8;
    padding: 12px 10px;
    vertical-align: middle;
    color: #223448;
    font-size: 0.92rem;
  }

  .table tbody td:last-child {
    border-right: none;
  }

  .table tbody tr:hover td {
    background: #f7fbff;
  }
`;

const StatusBadge = styled.span<{ $status?: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: capitalize;
  color: ${({ $status }) => {
    switch ($status) {
      case 'PROCESSED':
      case 'COMPLETED':
        return '#15803d';
      case 'PENDING':
        return '#b45309';
      case 'CANCELLED':
      case 'FAILED':
        return '#b91c1c';
      default:
        return '#475569';
    }
  }};
  background: ${({ $status }) => {
    switch ($status) {
      case 'PROCESSED':
      case 'COMPLETED':
        return '#dcfce7';
      case 'PENDING':
        return '#fef3c7';
      case 'CANCELLED':
      case 'FAILED':
        return '#fee2e2';
      default:
        return '#f1f5f9';
    }
  }};
  border: 1px solid ${({ $status }) => {
    switch ($status) {
      case 'PROCESSED':
      case 'COMPLETED':
        return '#86efac';
      case 'PENDING':
        return '#fde68a';
      case 'CANCELLED':
      case 'FAILED':
        return '#fca5a5';
      default:
        return '#cbd5e1';
    }
  }};
`;

const BetNumberTag = styled.span`
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  color: #1e293b;
  background: #f8fafc;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const PointsTag = styled.span`
  font-weight: 700;
  color: #166534;
  font-variant-numeric: tabular-nums;
`;

const ScheduledBetsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const [loading, setLoading] = useState(true);
  const [scheduledBets, setScheduledBets] = useState<ScheduledBet[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const fetchScheduledBets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dispatch(getScheduledBets()).unwrap();

      // Handle different response formats
      let bets = [];
      if (Array.isArray(response)) {
        bets = response;
      } else if (response && Array.isArray(response.data)) {
        bets = response.data;
      } else if (response && response.content) {
        bets = response.content;
      }

      setScheduledBets(bets || []);

      if (bets.length === 0) {
        showModalStrip('info', 'No scheduled bets found', 2000);
      }
    } catch (error: any) {
      console.error('Error fetching scheduled bets:', error);
      showModalStrip('danger', error?.message || 'Failed to fetch scheduled bets', 3000);
      setScheduledBets([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, showModalStrip]);

  useEffect(() => {
    fetchScheduledBets();
  }, [fetchScheduledBets]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const formatBetNumber = (betNumber: string, gameType?: string) => {
    if (gameType === 'Barfi' && betNumber) {
      const parts = betNumber.split('*');
      const placeholders = ['FD', 'LD', 'OP', 'EP', 'R', 'BARFI'];
      return parts
        .map((part, idx) => (part === '99999' ? placeholders[idx] || '' : part))
        .filter(Boolean)
        .join(' ');
    }
    return betNumber || '-';
  };

  const filteredBets = useMemo(() => {
    if (!searchQuery.trim()) return scheduledBets;
    const q = searchQuery.toLowerCase();
    return scheduledBets.filter((bet) => {
      const gameName = (bet.gameConfig?.gameJpa?.name || bet.game?.name || '').toLowerCase();
      const time = (bet.gameConfig?.resultTimeJpa?.resultTime || bet.resultTime?.resultTime || '').toLowerCase();
      const type = (bet.gameType || '').toLowerCase();
      const number = (bet.betNumber || '').toLowerCase();
      const status = (bet.status || '').toLowerCase();
      return gameName.includes(q) || time.includes(q) || type.includes(q) || number.includes(q) || status.includes(q);
    });
  }, [scheduledBets, searchQuery]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredBets.slice(startIndex, startIndex + pageSize).map((item, idx) => ({
      ...item,
      rowNumber: startIndex + idx + 1,
    }));
  }, [filteredBets, currentPage, pageSize]);

  const columns = [
    {
      header: '#',
      field: 'rowNumber',
      render: (bet: any) => bet.rowNumber,
    },
    {
      header: 'Date',
      field: 'createdAt',
      render: (bet: ScheduledBet) =>
        bet.createdAt ? format(new Date(bet.createdAt), 'dd-MMM-yyyy') : '-',
    },
    {
      header: 'Game',
      field: 'game',
      render: (bet: ScheduledBet) =>
        bet.gameConfig?.gameJpa?.name || bet.game?.name || 'N/A',
    },
    {
      header: 'Time',
      field: 'resultTime',
      render: (bet: ScheduledBet) =>
        bet.gameConfig?.resultTimeJpa?.resultTime || bet.resultTime?.resultTime || 'N/A',
    },
    {
      header: 'Type',
      field: 'gameType',
      render: (bet: ScheduledBet) => bet.gameType || '-',
    },
    {
      header: 'Number',
      field: 'betNumber',
      render: (bet: ScheduledBet) => (
        <BetNumberTag>{formatBetNumber(bet.betNumber, bet.gameType)}</BetNumberTag>
      ),
    },
    {
      header: 'Points',
      field: 'betPoint',
      render: (bet: ScheduledBet) => (
        <PointsTag>
          {Number(bet.betPoint || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </PointsTag>
      ),
    },
    {
      header: 'Status',
      field: 'status',
      render: (bet: ScheduledBet) => (
        <StatusBadge $status={bet.status?.toUpperCase()}>
          {bet.status ? bet.status.toLowerCase() : 'unknown'}
        </StatusBadge>
      ),
    },
  ];

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-3">
      <PageHeading>Scheduled Bets</PageHeading>

      <ControlsContainer>
        <SearchBox>
          <InputGroup.Text style={{ background: '#f8fafc', borderColor: '#d4deea' }}>
            <Search size={16} color="#64748b" />
          </InputGroup.Text>
          <FormControl
            placeholder="Search by game, time, number, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBox>
        <div style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: 500 }}>
          Total Bets: {filteredBets.length}
        </div>
      </ControlsContainer>

      <TableCard>
        <GenericTable
          columns={columns}
          data={paginatedData}
          pagination={filteredBets.length > pageSize}
          paginationData={{
            totalElements: filteredBets.length,
            pageSize: pageSize,
            currentPage: currentPage,
          }}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </TableCard>
    </Container>
  );
};

export default ScheduledBetsPage;
